import { MoreHorizontal } from 'lucide-react';
import { type MouseEvent, useRef, useState } from 'react';

import { CenterFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Menu from '@/components/Menu/Menu';
import type { TriggerPos } from '@/components/Select/types';
import type {
  Dashboard2FolderT,
  Dashboard2T,
} from '@/features/Dashboard/types/types';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services/index';
import useDropdownStore from '@/stores/pure/DropdownStore';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore, { useToast } from '@/stores/pure/ToastStore';
import { useRecentDashboardHistory } from '@/stores/WorkspaceCacheStore';
import { clipboardCopy } from '@/utils/util';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { useInvalidateDashboardListQueries } from '../hooks/useDashboardListQuery';
import DashboardFolderSelectModal from './Modals/DashboardFolderSelectModal';

type Props = {
  folders?: Dashboard2FolderT[];
  dashboard: Dashboard2T;
  searchValue: string | null;
  onEditDashboard: (dashboard: Dashboard2T) => void;
  refetch?: () => void;
};

const Dashboard2Action = (props: Props) => {
  const ref = useRef<HTMLButtonElement>(null);
  const addToast = useToast();
  const setPopup = usePopupStore((state) => state.setPopup);
  const { deleteHistory } = useRecentDashboardHistory();
  const wr = useWorkspaceRouter();
  const addDefaultToast = useToastStore((state) => state.addDefaultToast);
  const setCurrDropdown = useDropdownStore((state) => state.setCurrDropdown);
  const invalidateDashboardLists = useInvalidateDashboardListQueries();
  const [selectModalPos, setSelectModalPos] = useState<TriggerPos>({
    top: 0,
    left: 0,
  });

  const DASHBOARD_SELECT_MODAL_KEY = `DASHBAORD2_SELECT_MODAL_ROW_KEY_${props.dashboard.alias}`;

  const handleDelete = async () => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete dashboard',
      message: 'Are you sure you want to delete this dashboard?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        await service.dashboard2.deleteDashboard(props.dashboard.alias);
        deleteHistory([props.dashboard.alias]);
        await invalidateDashboardLists();
        addToast('Dashboard Deleted', 'success');
        props.refetch?.();
      },
    });
  };

  const handleRename = () => {
    props.onEditDashboard(props.dashboard);
  };

  const handleShare = () => {
    const wa = getCurrWorkspaceAlias();
    const newUrl = `${window.location.origin}/workspace/${wa}/dashboard2/${props.dashboard.alias}`;
    clipboardCopy(newUrl);
    addToast('Dashboard link Copied', 'success');
  };

  const handleDuplicate = async () => {
    try {
      const duplicatedDashboard = await service.dashboard2.duplicateDashboard(
        props.dashboard.alias,
      );

      wr.push(`/dashboard2/${duplicatedDashboard.alias}`);
    } catch (e: unknown) {
      addDefaultToast('Dashboard duplication failed', 'fail');
    }
  };

  const handleMoveTo = (e: MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setSelectModalPos({
      top: rect.top,
      left: rect.right,
    });
    setCurrDropdown(DASHBOARD_SELECT_MODAL_KEY);
  };

  const handleSelectDashboardFolder = async (folderAlias: string) => {
    try {
      if (folderAlias === 'all') {
        await service.dashboard2.moveDashboartToRoot([props.dashboard.alias]);
      } else {
        await service.dashboard2.moveDashboardToFolder(folderAlias, [
          props.dashboard.alias,
        ]);
      }

      await invalidateDashboardLists();
      addToast('Dashboard has been moved', 'success');
      props.refetch?.();
    } catch (e) {
      addToast('Error! try again', 'fail');
    }
  };

  return (
    <CenterFlex width="100%">
      <Menu
        triggerNode={
          <Button
            ref={ref}
            variant="ghost_secondary"
            sizeType="s"
            leftIcon={<MoreHorizontal />}
          />
        }
        options={[
          {
            label: 'Move to',
            onClick: handleMoveTo,
          },
          {
            label: 'Rename',
            onClick: handleRename,
          },
          {
            label: 'Share',
            onClick: handleShare,
          },
          {
            label: 'Duplicate',
            onClick: handleDuplicate,
          },
          {
            label: 'Delete',
            onClick: handleDelete,
          },
        ]}
      />
      <DashboardFolderSelectModal
        name={DASHBOARD_SELECT_MODAL_KEY}
        currentFolder={props.dashboard.folder_id}
        pos={selectModalPos}
        onSelect={handleSelectDashboardFolder}
        folders={props.folders}
      />
    </CenterFlex>
  );
};

export default Dashboard2Action;
